import { line as d3Line, Line } from 'd3-shape';

import { Layout } from '../layout/layout';
import { ArrangementType } from '../models/emuns';
import { IChannel, ISample } from '../models/ichannel';
import { ECG_CONSTANTS } from '../utils/ecg-constants';
import { Scaler } from '../utils/scaler';
import { CaliperViewModel } from './caliper-viewmodel';
import { IChartContext } from './chart-viewmodel';
import { Point } from './common-models';

export class ArrangementConfiguration {
    leadCount: number = 0;
    derivedWaveformPresent: boolean = false;
}

class ChannelSelector {
    // data members

    get currentChannel() {
        return this._layout.ecgState.chartState.arrangement == ArrangementType.Medians
            ? this._derivedChannel
            : this._originalChannel;
    }

    private _originalChannel: IChannel | null;
    get originalChannel() {
        return this._originalChannel;
    }

    private _derivedChannel: IChannel | null;
    get derivedChannel() {
        return this._derivedChannel;
    }

    private _layout: Layout;

    // constructor

    constructor(originalChannel: IChannel | null, derivedChannel: IChannel | null, layout: Layout) {
        this._originalChannel = originalChannel;
        this._derivedChannel = derivedChannel;
        this._layout = layout;
    }
}

export class TraceData {
    originalChannel: IChannel | null = null;
    derivedChannel: IChannel | null = null;
    markers = new Array<number>();
}

export class Appearance {
    private readonly _defaultColor: string = 'black';

    constructor() {
        this.setDefaultColor();
    }

    setDefaultColor() {
        this.color = this._defaultColor;
    }

    color: string = '';
    highlight: boolean = false;
}

export interface TimeRange {
    start: number;
    end: number;
}

export class TraceViewModel {
    // data members

    title: TitleViewModel | null = null;
    step: StepViewModel | null = null;

    private _calipers = new Array<CaliperViewModel>();
    get calipers() {
        return this._calipers;
    }

    private _originalFiducialPoints: number[] = [];
    fiducialPoints = new Array<FiducialPoint>();

    layout: Layout | null = null;

    appearance: Appearance = new Appearance();

    get channel() {
        return this.channelSelector?.currentChannel ?? null;
    }

    private _channelSelector: ChannelSelector | null = null;
    get channelSelector() {
        return this._channelSelector;
    }

    samples: string = '';
    translate: string = '';
    visible: boolean = false;
    selected: boolean = false;

    scaler: Scaler | null = null;

    protected _windowedSamples = new Array<ISample>();
    get windowedSamples() {
        return this._windowedSamples;
    }

    private scaleLine: Line<ISample> | null = null;

    private range: TimeRange = { start: 0, end: 10000 };

    // constructor

    constructor(layout: Layout, traceData: TraceData) {
        if (!layout.context) return;
        this._channelSelector = new ChannelSelector(traceData.originalChannel, traceData.derivedChannel, layout);

        this.layout = layout;

        this._originalFiducialPoints = traceData.markers;

        this._windowedSamples = this.channel?.samples ?? [];

        this.updateScale();

        this.title = new TitleViewModel(this.channel?.name ?? '');
        this.step = new StepViewModel(layout.context, this.channel);

        this.updateTrace();
    }

    // public functions

    updateTraceTimeAlignment(range: TimeRange) {
        this.range = range;

        this._windowedSamples = this.channel?.samples.slice(range.start, range.end) ?? [];

        this.updateTrace();
    }

    updateTrace() {
        const leadHeight = this.layout?.context?.leadHeight ?? 0;
        this.scaleLine = this.updateScale();
        this.samples = this.scaleLine(this.windowedSamples) ?? '';

        this.fiducialPoints = new Array<FiducialPoint>();

        for (const fiducialPoint of this._originalFiducialPoints) {
            if (!!this.scaler && fiducialPoint >= this.range.start && fiducialPoint <= this.range.end) {
                this.fiducialPoints.push(new FiducialPoint(fiducialPoint - this.range.start, this.scaler, leadHeight));
            }
        }

        this.fiducialPoints.forEach((fiducialPoint) => fiducialPoint.update());
    }

    // protected functions

    protected updateScale() {
        const leadWidth = this.layout?.context?.leadWidth ?? 0;
        const leadHeight = this.layout?.context?.leadHeight ?? 0;
        const scaleSettingsX = {
            domain: { min: 0, max: this.windowedSamples.length },
            range: { min: 0, max: leadWidth },
        };
        const scaleSettingsY = {
            domain: {
                min: -ECG_CONSTANTS.MAX_POSITIVE_MICROVOLT_VALUE,
                max: ECG_CONSTANTS.MAX_POSITIVE_MICROVOLT_VALUE,
            },
            range: { min: leadHeight, max: 0 },
        };
        this.scaler = new Scaler(scaleSettingsX, scaleSettingsY);
        const scaler = this.scaler;
        let line = d3Line<ISample>()
            .x((_sample: ISample, index) => {
                return scaler.onX(index);
            })
            .y((sample: ISample) => {
                return scaler.onY(sample.value);
            });

        return line;
    }
}

export class ReferenceTraceViewModel extends TraceViewModel {
    // constructor

    constructor(layout: Layout, channel: IChannel) {
        let traceData = new TraceData();
        traceData.originalChannel = channel;
        traceData.derivedChannel = channel;

        super(layout, traceData);

        if (this.step) this.step.title = 'Reference ' + channel.name;
    }

    // public functions

    updateTraceTimeAlignment(_: { start: number; end: number }) {
        this._windowedSamples = this.channel?.samples ?? [];
        this.updateTrace();
    }

    // protected functions

    protected updateScale() {
        const leadWidth = this.layout?.context?.leadWidth ?? 0;
        const leadHeight = this.layout?.context?.leadHeight ?? 0;
        const scaleSettingsX = {
            domain: { min: 0, max: this.windowedSamples.length },
            range: { min: 0, max: 4 * leadWidth },
        };
        const scaleSettingsY = {
            domain: {
                min: -ECG_CONSTANTS.MAX_POSITIVE_MICROVOLT_VALUE,
                max: ECG_CONSTANTS.MAX_POSITIVE_MICROVOLT_VALUE,
            },
            range: { min: leadHeight, max: 0 },
        };
        this.scaler = new Scaler(scaleSettingsX, scaleSettingsY);
        const scaler = this.scaler;
        let line = d3Line<ISample>()
            .x((_sample: ISample, index) => {
                return scaler.onX(index);
            })
            .y((sample: ISample) => {
                return scaler.onY(sample.value);
            });

        return line;
    }
}

export class FiducialPoint {
    // data members

    marker: string | null;

    private _marker = new Array<Point>();

    private value: number;

    private scaler: Scaler;
    private scaleLine: Line<Point>;

    // constructor

    constructor(value: number, scaler: Scaler, height: number) {
        this.value = value;
        this.scaler = scaler;

        this._marker.push(new Point(value, height / 2 + 10));
        this._marker.push(new Point(value, height / 2 - 10));
        this.scaleLine = this.updateScale();
        this.marker = this.scaleLine(this._marker);
    }

    // public functions

    update() {
        this.scaleLine = this.updateScale();
        this.marker = this.scaleLine(this._marker);
    }

    // private functions

    private updateScale(): Line<Point> {
        return d3Line<Point>()
            .x((point: Point) => {
                return this.scaler.onX(point.x);
            })
            .y((point: Point) => {
                return point.y;
            });
    }
}

export class TitleViewModel {
    // data members

    text: string;
    visible: boolean;

    // constructor

    constructor(text: string) {
        this.text = text;
        this.visible = true;
    }
}

export class StepViewModel {
    // data members

    path;
    titleY: number;
    title: string;

    // constructor

    constructor(chartContext: IChartContext, channel: IChannel | null) {
        let points = new Array<Point>();
        points.push({ x: -15, y: chartContext.leadHeight / 2 });
        points.push({ x: -10, y: chartContext.leadHeight / 2 });

        points.push({ x: -10, y: chartContext.leadHeight / 2 });
        points.push({ x: -10, y: chartContext.leadHeight / 2 - 10 });

        points.push({ x: -10, y: chartContext.leadHeight / 2 - 10 });
        points.push({ x: -5, y: chartContext.leadHeight / 2 - 10 });

        points.push({ x: -5, y: chartContext.leadHeight / 2 - 10 });
        points.push({ x: -5, y: chartContext.leadHeight / 2 });

        points.push({ x: -5, y: chartContext.leadHeight / 2 });
        points.push({ x: 0, y: chartContext.leadHeight / 2 });

        let line = d3Line<Point>()
            .x((point: Point) => {
                return point.x;
            })
            .y((point: Point) => {
                return point.y;
            });

        this.path = line(points);

        this.title = channel?.name ?? '';
        this.titleY = chartContext.leadHeight / 2 + 5;
    }
}

export class PinViewModel {
    // data members

    position: Point | null = null;
    opacity: number = 0;

    private chartContext: IChartContext;

    // constructor

    constructor(chartContext: IChartContext) {
        this.chartContext = chartContext;

        this.update(this.chartContext.leadWidth);
    }

    // public functions

    update(x: number) {
        this.position = {
            x: x + 5,
            y: this.chartContext.leadHeight / 2 - 5,
        };
    }
}
